import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { useRef } from "react";
import Hero from "@/components/Hero";
import Marquee from "@/components/Marquee";
import Faq, { faqJsonLd } from "@/components/Faq";
import Seo from "@/components/Seo";
import { Rise, MaskReveal, Overline } from "@/components/Reveal";
import { HOME } from "@/constants/testIds";

const pillars = [
  {
    key: "abattoir",
    testId: HOME.pillarAbattoir,
    number: "I",
    title: "The Abattoir",
    body: "Over a hundred years of trading, decades of training, and an in-house Quality Assurance team monitoring CCTV and every product line. Welfare and handling are audited daily — nothing leaves the yard that hasn't met our standards.",
    hours: "Full traceability · Farm to dispatch",
    href: "/about",
    cta: "Read the story",
    image: "https://images.pexels.com/photos/31794126/pexels-photo-31794126.jpeg?auto=compress&cs=tinysrgb&w=1200",
  },
  {
    key: "wholesale",
    testId: HOME.pillarWholesale,
    number: "II",
    title: "Wholesale",
    body: "A modern refrigerated fleet delivers beef and lamb to restaurants, farm shops and independent butchers across the South West. We work best with kitchens who care where their meat comes from — and want the same names to arrive every week.",
    hours: "Accounts by application",
    href: "/wholesale",
    cta: "Open an account",
    image: "https://images.pexels.com/photos/8314505/pexels-photo-8314505.jpeg?auto=compress&cs=tinysrgb&w=1200",
  },
];

function Pillars() {
  return (
    <section className="relative py-16 md:py-40 bg-[color:var(--cream)]">
      <div className="mx-auto max-w-[1600px] px-5 md:px-10">
        <div className="grid grid-cols-12 gap-6 md:gap-12 mb-12 md:mb-32">
          <Rise className="col-span-12 md:col-span-4">
            <Overline>Three pillars · One family</Overline>
          </Rise>
          <Rise delay={0.1} className="col-span-12 md:col-span-8">
            <h2 className="mt-3 md:mt-0 font-display text-[10vw] sm:text-5xl md:text-6xl lg:text-7xl leading-[0.95] tracking-tightest text-[color:var(--ink)]">
              A single yard.<br />
              <span className="italic text-[color:var(--oxblood)]">Two trades</span> under one roof.
            </h2>
          </Rise>
        </div>

        <div className="flex flex-col gap-16 md:gap-40">
          {pillars.map((p, idx) => (
            <Pillar key={p.key} pillar={p} idx={idx} />
          ))}
        </div>
      </div>
    </section>
  );
}

function Pillar({ pillar, idx }) {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], [40, -80]);
  const flip = idx % 2 === 1;

  return (
    <div ref={ref} data-testid={pillar.testId} className="grid grid-cols-12 gap-6 md:gap-10 items-center">
      <motion.div
        style={{ y }}
        className={`col-span-12 md:col-span-6 relative ${flip ? "md:order-2" : "md:order-1"}`}
      >
        <div className="clip-frame grain aspect-[4/5] md:aspect-[3/4] w-full">
          <img src={pillar.image} alt={pillar.title} className="w-full h-full object-cover" />
        </div>
        <div className={`absolute -top-4 md:-top-6 ${flip ? "right-2 md:-right-6" : "left-2 md:-left-6"} font-display italic text-5xl md:text-8xl text-[color:var(--oxblood)]`}>
          {pillar.number}
        </div>
      </motion.div>

      <div className={`col-span-12 md:col-span-6 ${flip ? "md:order-1 md:pr-16" : "md:order-2 md:pl-16"}`}>
        <Rise>
          <Overline>Chapter {pillar.number}</Overline>
          <h3 className="mt-3 md:mt-4 font-display text-[10vw] sm:text-4xl md:text-6xl lg:text-7xl leading-[0.95] tracking-tightest text-[color:var(--ink)]">
            {pillar.title}
          </h3>
        </Rise>
        <Rise delay={0.05}>
          <p className="mt-6 md:mt-8 font-body text-base md:text-2xl leading-relaxed text-[color:var(--ink-light)] max-w-lg">
            {pillar.body}
          </p>
        </Rise>
        <Rise delay={0.1}>
          <div className="mt-6 md:mt-8 font-ui text-[10px] md:text-xs uppercase tracking-editorial text-[color:var(--ink-light)]">
            {pillar.hours}
          </div>
        </Rise>
        <Rise delay={0.15}>
          <div className="mt-8 md:mt-10">
            <Link to={pillar.href} className="btn-sharp-outline">
              {pillar.cta}
              <svg width="14" height="10" viewBox="0 0 14 10" fill="none"><path d="M0 5h13M9 1l4 4-4 4" stroke="currentColor" strokeWidth="1.2" /></svg>
            </Link>
          </div>
        </Rise>
      </div>
    </div>
  );
}

const manifesto = [
  { num: "01", title: "Welfare, above all", body: "CCTV across every production stage, monitored in-house by our Quality Assurance team. Not decoration — a working practice, audited and recorded." },
  { num: "02", title: "Full traceability", body: "Every animal is tracked from arrival to dispatch. If it's on our block, we know the farm, the field and the day it came in." },
  { num: "03", title: "Trained hands", body: "Around thirty-five people work here. Many of them have been with us over a decade — because good butchery is a craft, not a shift." },
  { num: "04", title: "Kept close", body: "The abattoir, the ageing rooms, the block and the shop counter all sit inside the same Wiltshire yard. Short journeys. Longer flavour." },
];

function Manifesto() {
  return (
    <section data-testid={HOME.manifestoSection} className="relative py-16 md:py-40 bg-[color:var(--paper)] border-y border-[color:var(--hairline)]">
      <div className="mx-auto max-w-[1600px] px-5 md:px-10">
        <div className="grid grid-cols-12 gap-6 mb-10 md:mb-24">
          <Rise className="col-span-12 md:col-span-4"><Overline>Our manifesto</Overline></Rise>
          <Rise delay={0.1} className="col-span-12 md:col-span-8">
            <h2 className="mt-3 md:mt-0 font-display text-[10vw] sm:text-5xl md:text-6xl lg:text-7xl leading-[0.95] tracking-tightest">
              Four rules we <span className="italic text-[color:var(--oxblood)]">don't</span> bend.
            </h2>
          </Rise>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-x-16 gap-y-10 md:gap-y-24">
          {manifesto.map((m, i) => (
            <Rise key={m.num} delay={i * 0.05}>
              <div className="grid grid-cols-12 gap-3 md:gap-4 items-start">
                <div className="col-span-4 md:col-span-4">
                  <div className="chapter-number text-[16vw] sm:text-[14vw] md:text-[9vw] lg:text-[8vw]">
                    {m.num}
                  </div>
                </div>
                <div className="col-span-8 md:col-span-8 pt-2 md:pt-10">
                  <h3 className="font-display text-xl sm:text-2xl md:text-3xl italic text-[color:var(--ink)]">{m.title}</h3>
                  <p className="mt-3 md:mt-4 font-body text-base md:text-xl text-[color:var(--ink-light)] leading-relaxed">{m.body}</p>
                </div>
              </div>
            </Rise>
          ))}
        </div>
      </div>
    </section>
  );
}

function Story() {
  return (
    <section data-testid={HOME.storySection} className="relative py-16 md:py-40 bg-[color:var(--cream)]">
      <div className="mx-auto max-w-[1600px] px-5 md:px-10 grid grid-cols-12 gap-6 md:gap-16 items-center">
        <div className="col-span-12 md:col-span-6">
          <Rise>
            <div className="clip-frame grain aspect-[4/5] w-full max-w-lg mx-auto md:mx-0">
              <img
                src="https://images.unsplash.com/photo-1700478934617-75d2d4f01a04?auto=format&fit=crop&w=1200&q=80"
                alt="A butcher preparing meat"
                className="w-full h-full object-cover"
              />
            </div>
          </Rise>
        </div>
        <div className="col-span-12 md:col-span-6">
          <Rise>
            <Overline>Tockenham Corner · SN4 7PF</Overline>
            <h2 className="mt-4 md:mt-6 font-display text-[9vw] sm:text-4xl md:text-5xl lg:text-6xl leading-[0.95] tracking-tightest">
              A yard that has fed <span className="italic text-[color:var(--oxblood)]">Wiltshire</span> for four generations.
            </h2>
          </Rise>
          <Rise delay={0.1}>
            <p className="mt-6 md:mt-8 font-body text-base md:text-xl leading-relaxed text-[color:var(--ink-light)]">
              F. Drury &amp; Sons has been trading for over a hundred years. In that time we've seen BSE, Foot and Mouth, and every fluctuation of British farming — and quietly kept doing what we do. Modern refrigerated vehicles now leave the yard every morning, but the standards on the block have never really changed.
            </p>
          </Rise>
          <Rise delay={0.15}>
            <div className="mt-8 md:mt-10 flex flex-wrap gap-3">
              <Link to="/about" className="btn-sharp">Read the heritage</Link>
              <a href="https://www.facebook.com/TockenhamMeats" target="_blank" rel="noopener noreferrer" className="btn-sharp-outline">Follow the shop</a>
            </div>
          </Rise>
        </div>
      </div>
    </section>
  );
}

function CTA() {
  return (
    <section className="relative py-16 md:py-32 bg-[color:var(--oxblood)] text-[color:var(--cream)] overflow-hidden">
      <div className="mx-auto max-w-[1600px] px-5 md:px-10 grid grid-cols-12 gap-6 md:gap-8 items-end">
        <div className="col-span-12 md:col-span-8">
          <Rise>
            <span className="font-ui text-[11px] uppercase tracking-editorial text-[color:var(--mustard)]">Come in, or write in</span>
            <h2 className="mt-4 md:mt-6 font-display text-[12vw] sm:text-6xl md:text-7xl lg:text-8xl leading-[0.9] tracking-tightest">
              We'd rather<br /><span className="italic">shake hands.</span>
            </h2>
          </Rise>
        </div>
        <div className="col-span-12 md:col-span-4 flex flex-col gap-4 md:items-end mt-6 md:mt-0">
          <Rise delay={0.1}>
            <div className="font-body italic text-base md:text-lg text-[color:var(--cream)]/80 md:text-right max-w-xs">
              Whatever the enquiry — a Sunday roast, a wholesale account, a question about traceability — the phone rings straight through.
            </div>
          </Rise>
          <Rise delay={0.15}>
            <div className="flex gap-3 flex-wrap">
              <a href="tel:01793852467" className="btn-sharp" style={{ background: "var(--cream)", color: "var(--ink)", borderColor: "var(--cream)" }}>Call 01793 852467</a>
              <Link to="/contact" className="btn-sharp-outline" style={{ borderColor: "var(--cream)", color: "var(--cream)" }}>Send a message</Link>
            </div>
          </Rise>
        </div>
      </div>
    </section>
  );
}

export default function Home() {
  return (
    <main>
      <Seo
        title="F. Drury & Sons — Wiltshire Abattoir & Wholesale Butcher"
        description="Family-run Wiltshire abattoir and wholesale butcher, trading over 100 years from Tockenham near Swindon. Beef and lamb — full farm-to-dispatch traceability. Wholesale accounts across the South West."
        path="/"
        keywords="F Drury and Sons, Drury butcher Swindon, abattoir Wiltshire, wholesale butcher Swindon, butcher Wootton Bassett, Tockenham abattoir, wholesale meat Wiltshire, family butcher, traceability"
        schema={[
          faqJsonLd,
          {
            "@context": "https://schema.org",
            "@type": "WebPage",
            "@id": "https://fdruryandsons.co.uk/#webpage",
            url: "https://fdruryandsons.co.uk/",
            name: "F. Drury & Sons — Wiltshire Abattoir & Wholesale Butcher",
            isPartOf: { "@id": "https://fdruryandsons.co.uk/#website" },
            about: { "@id": "https://fdruryandsons.co.uk/#localbusiness" },
            primaryImageOfPage: {
              "@type": "ImageObject",
              url: "https://images.unsplash.com/photo-1625604086988-6e41981275fa?auto=format&fit=crop&w=1600&q=80",
            },
            inLanguage: "en-GB",
            speakable: {
              "@type": "SpeakableSpecification",
              cssSelector: ["h1", "[data-testid=home-hero-heading]", "#faq"],
            },
          },
        ]}
      />
      <Hero />
      <Marquee />
      <Pillars />
      <Manifesto />
      <Story />
      <Faq />
      <CTA />
    </main>
  );
}
